import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

import hisselerVeri from '../data/bist-hisseler.json';
import { gecmisFiyatGetir, fiyatAkisiAc } from '../services/isyatirimApi';
import { fiyatFormatla, isaretliFormatla, degisimHesapla } from '../utils/fiyat';

const TUM_HISSELER = hisselerVeri.data;
const GRAFIK_GENISLIK = 640;
const GRAFIK_YUKSEKLIK = 260;
// Sağ: fiyat etiketleri, alt: tarih etiketleri için ayrılan boşluk.
const GRAFIK_PADDING = { sol: 8, sag: 56, ust: 16, alt: 28 };
const Y_EKSENI_ADIM_SAYISI = 4;
const X_EKSENI_ETIKET_SAYISI = 7;
const GRAFIK_RENGI = '#f97316';

// Bağımlılıksız, minimalist bir çizgi grafik: 200 günlük kapanış verisini
// tek bir SVG polyline olarak çiziyor. Sağ eksende fiyat, alt eksende eşit
// aralıklı tarih etiketleri gösterir; fare ile üzerine gelindiğinde dikey
// bir işaretçi çizgisi, vurgulanmış nokta ve tarih/fiyat kutusu belirir.
// Verilen gün sayısı kadar geriye giden aralıktaki kapanışların en
// düşük/en yüksek değerini ve aralık başındaki fiyata göre güncel
// fiyata olan yüzde değişimini bulur (haftalık/aylık özet için).
function araliktaOzet(veri, gunSayisi, guncelFiyat) {
  const simdi = veri[veri.length - 1].tarih;
  const baslangic = new Date(simdi.getTime() - gunSayisi * 24 * 60 * 60 * 1000);
  const araliktakiler = veri.filter(v => v.tarih >= baslangic);

  if (araliktakiler.length === 0) return null;

  const kapanislar = araliktakiler.map(v => v.kapanis);
  return {
    enDusuk: Math.min(...kapanislar),
    enYuksek: Math.max(...kapanislar),
    degisim: degisimHesapla(guncelFiyat, araliktakiler[0].kapanis)
  };
}

// Değişim yüzdesine göre kar/zarar rengi (>= 0 yeşil, negatif kırmızı).
function degisimRengi(yuzde) {
  return yuzde >= 0 ? 'text-emerald-600' : 'text-red-600';
}

function HisseGrafik({ veri, hisseKodu, anlikFiyat, gunlukDegisim }) {
  const [hoverIndex, setHoverIndex] = useState(null);

  const kapanislar = veri.map(v => v.kapanis);
  const enDusuk = Math.min(...kapanislar);
  const enYuksek = Math.max(...kapanislar);
  const araligi = enYuksek - enDusuk || 1;

  // Yüzde değişimler güncel (anlık) fiyata göre hesaplanır; akış henüz
  // bağlanmadıysa son geçmiş kapanış geçici olarak kullanılır.
  const guncelFiyat = anlikFiyat !== null && anlikFiyat !== undefined ? anlikFiyat : veri[veri.length - 1].kapanis;

  const haftalik = araliktaOzet(veri, 7, guncelFiyat);
  const aylik = araliktaOzet(veri, 30, guncelFiyat);
  const genelDegisim = degisimHesapla(guncelFiyat, veri[0].kapanis);

  const cizimGenisligi = GRAFIK_GENISLIK - GRAFIK_PADDING.sol - GRAFIK_PADDING.sag;
  const cizimYuksekligi = GRAFIK_YUKSEKLIK - GRAFIK_PADDING.ust - GRAFIK_PADDING.alt;

  const xHesapla = i => veri.length > 1
    ? GRAFIK_PADDING.sol + (i / (veri.length - 1)) * cizimGenisligi
    : GRAFIK_PADDING.sol + cizimGenisligi / 2;
  const yHesapla = kapanis => GRAFIK_PADDING.ust + (1 - (kapanis - enDusuk) / araligi) * cizimYuksekligi;

  const cizgiYolu = veri
    .map((v, i) => `${i === 0 ? 'M' : 'L'}${xHesapla(i).toFixed(2)},${yHesapla(v.kapanis).toFixed(2)}`)
    .join(' ');

  // Y ekseni: en düşük ile en yüksek arasında eşit aralıklı fiyat çizgileri.
  const yEkseniDegerleri = Array.from({ length: Y_EKSENI_ADIM_SAYISI + 1 }, (_, i) => enDusuk + (araligi * i) / Y_EKSENI_ADIM_SAYISI);

  // X ekseni: veri boyunca eşit aralıklarla seçilmiş noktalara gün+ay etiketi.
  const xEkseniEtiketleri = Array.from({ length: X_EKSENI_ETIKET_SAYISI }, (_, i) => {
    const oran = X_EKSENI_ETIKET_SAYISI > 1 ? i / (X_EKSENI_ETIKET_SAYISI - 1) : 0;
    const index = Math.round(oran * (veri.length - 1));
    return { x: xHesapla(index), etiket: veri[index].tarih.toLocaleDateString('tr-TR', { day: '2-digit', month: 'long' }) };
  });

  function fareHareket(event) {
    const rect = event.currentTarget.getBoundingClientRect();
    const oranX = (event.clientX - rect.left) / rect.width;
    const xViewBox = oranX * GRAFIK_GENISLIK;
    const index = Math.round(((xViewBox - GRAFIK_PADDING.sol) / cizimGenisligi) * (veri.length - 1));
    setHoverIndex(Math.max(0, Math.min(veri.length - 1, index)));
  }

  const hoverNokta = hoverIndex !== null ? veri[hoverIndex] : null;
  const hoverX = hoverNokta ? xHesapla(hoverIndex) : null;
  const hoverY = hoverNokta ? yHesapla(hoverNokta.kapanis) : null;
  const tooltipSolda = hoverX !== null && hoverX > GRAFIK_GENISLIK * 0.6;

  return (
    <div>
      <div className="rounded-xl bg-slate-50 p-4">
        <div className="relative">
          <svg viewBox={`0 0 ${GRAFIK_GENISLIK} ${GRAFIK_YUKSEKLIK}`} className="w-full">
            {yEkseniDegerleri.map(deger => (
              <g key={deger}>
                <line
                  x1={GRAFIK_PADDING.sol} x2={GRAFIK_GENISLIK - GRAFIK_PADDING.sag}
                  y1={yHesapla(deger)} y2={yHesapla(deger)}
                  stroke="#e2e8f0" strokeWidth="1"
                />
                <text x={GRAFIK_GENISLIK - GRAFIK_PADDING.sag + 8} y={yHesapla(deger)} textAnchor="start" dominantBaseline="middle" fontSize="11" fill="#64748b">
                  {fiyatFormatla(deger)}
                </text>
              </g>
            ))}

            <path d={cizgiYolu} fill="none" stroke={GRAFIK_RENGI} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />

            {xEkseniEtiketleri.map(({ x, etiket }) => (
              <text key={etiket + x} x={x} y={GRAFIK_YUKSEKLIK - 8} textAnchor="middle" fontSize="11" fill="#64748b">
                {etiket}
              </text>
            ))}

            {hoverNokta && (
              <>
                <line x1={hoverX} x2={hoverX} y1={GRAFIK_PADDING.ust} y2={GRAFIK_YUKSEKLIK - GRAFIK_PADDING.alt} stroke="#94a3b8" strokeWidth="1" />
                <circle cx={hoverX} cy={hoverY} r="5" fill={GRAFIK_RENGI} stroke="#fff" strokeWidth="2" />
              </>
            )}

            <rect
              x="0" y="0" width={GRAFIK_GENISLIK} height={GRAFIK_YUKSEKLIK}
              fill="transparent"
              onMouseMove={fareHareket}
              onMouseLeave={() => setHoverIndex(null)}
            />
          </svg>

          {hoverNokta && (
            <div
              className="absolute top-2 pointer-events-none whitespace-nowrap rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs shadow-lg"
              style={{
                left: `${(hoverX / GRAFIK_GENISLIK) * 100}%`,
                transform: tooltipSolda ? 'translateX(-105%)' : 'translateX(5%)'
              }}
            >
              <p className="text-slate-500">
                {hoverNokta.tarih.toLocaleDateString('tr-TR', { day: '2-digit', month: 'long', year: 'numeric' })}{' '}
                {hoverNokta.tarih.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit', hour12: false })}
              </p>
              <p className="font-bold text-orange-600">{hisseKodu}: {fiyatFormatla(hoverNokta.kapanis)}</p>
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-4 gap-3 mt-3">
        <div className="rounded-lg bg-slate-50 px-3 py-2">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Günlük</p>
          <p className={'mt-1 text-xs font-semibold ' + (gunlukDegisim ? degisimRengi(gunlukDegisim.yuzde) : 'text-slate-600')}>
            {gunlukDegisim ? isaretliFormatla(gunlukDegisim.yuzde, '%') : '—'}
          </p>
        </div>
        <div className="rounded-lg bg-slate-50 px-3 py-2">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Haftalık</p>
          <p className="mt-1 text-xs text-slate-600">
            {haftalik ? `${fiyatFormatla(haftalik.enDusuk)} - ${fiyatFormatla(haftalik.enYuksek)}` : '—'}
          </p>
          <p className={'text-xs font-semibold ' + (haftalik?.degisim ? degisimRengi(haftalik.degisim.yuzde) : 'text-slate-600')}>
            {haftalik?.degisim ? isaretliFormatla(haftalik.degisim.yuzde, '%') : '—'}
          </p>
        </div>
        <div className="rounded-lg bg-slate-50 px-3 py-2">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Aylık</p>
          <p className="mt-1 text-xs text-slate-600">
            {aylik ? `${fiyatFormatla(aylik.enDusuk)} - ${fiyatFormatla(aylik.enYuksek)}` : '—'}
          </p>
          <p className={'text-xs font-semibold ' + (aylik?.degisim ? degisimRengi(aylik.degisim.yuzde) : 'text-slate-600')}>
            {aylik?.degisim ? isaretliFormatla(aylik.degisim.yuzde, '%') : '—'}
          </p>
        </div>
        <div className="rounded-lg bg-slate-50 px-3 py-2">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">200 Günlük</p>
          <p className="mt-1 text-xs text-slate-600">{fiyatFormatla(enDusuk)} - {fiyatFormatla(enYuksek)}</p>
          <p className={'text-xs font-semibold ' + (genelDegisim ? degisimRengi(genelDegisim.yuzde) : 'text-slate-600')}>
            {genelDegisim ? isaretliFormatla(genelDegisim.yuzde, '%') : '—'}
          </p>
        </div>
      </div>
    </div>
  );
}

function HisseDetay() {
  const { kod: kodParam } = useParams();
  const kod = (kodParam || '').toUpperCase();
  const hisse = useMemo(() => TUM_HISSELER.find(h => h.kod === kod), [kod]);

  const [veri, setVeri] = useState([]);
  const [veriYukleniyor, setVeriYukleniyor] = useState(true);
  const [veriHatasi, setVeriHatasi] = useState('');

  const [anlikFiyat, setAnlikFiyat] = useState(null);
  const [dayClose, setDayClose] = useState(null);
  const [guncellemeZamani, setGuncellemeZamani] = useState(null);

  useEffect(() => {
    if (!hisse) return;
    let iptalEdildi = false;

    setVeriYukleniyor(true);
    setVeriHatasi('');

    gecmisFiyatGetir(kod)
      .then(sonuc => {
        if (iptalEdildi) return;
        setVeri(sonuc);
        setVeriYukleniyor(false);
      })
      .catch(err => {
        if (iptalEdildi) return;
        setVeriHatasi(err.message || 'Geçmiş fiyat verisi alınamadı');
        setVeriYukleniyor(false);
      });

    return () => { iptalEdildi = true; };
  }, [kod, hisse]);

  useEffect(() => {
    if (!hisse) return;
    return fiyatAkisiAc([kod], (_, veriGuncel) => {
      setAnlikFiyat(veriGuncel.last);
      setDayClose(veriGuncel.dayClose);
      setGuncellemeZamani(veriGuncel.guncellemeZamani);
    });
  }, [kod, hisse]);

  const degisim = anlikFiyat !== null && dayClose !== null ? degisimHesapla(anlikFiyat, dayClose) : null;
  const renkSinifi = degisim && degisim.fark < 0 ? 'text-red-600' : 'text-emerald-600';

  if (!hisse) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
        <p className="text-sm text-slate-500">"{kodParam}" için bir hisse bulunamadı.</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
        <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">{hisse.kod}</p>
        <h1 className="text-lg font-bold text-slate-800 mb-4">{hisse.ad}</h1>

        <div className="flex items-end justify-between rounded-xl bg-slate-900 px-6 py-5">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Anlık Fiyat</p>
            <p className="mt-1 text-3xl font-black text-white">
              {anlikFiyat !== null ? fiyatFormatla(anlikFiyat) : '—'}
            </p>
            {guncellemeZamani && (
              <p className="mt-1 text-xs text-slate-400">{guncellemeZamani.toLocaleTimeString('tr-TR')} güncellendi</p>
            )}
          </div>
          {degisim && (
            <span className={
              'rounded-full px-3 py-1 text-sm font-semibold ' +
              (degisim.fark < 0 ? 'bg-red-500/10 text-red-400' : 'bg-emerald-500/10 text-emerald-400')
            }>
              {isaretliFormatla(degisim.yuzde, '%')}
            </span>
          )}
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
        <div className="text-lg font-bold text-slate-800 mb-4">Son 200 Günlük Kapanış Fiyatları</div>

        {veriHatasi && <p className="text-sm text-red-600 mb-3">{veriHatasi}</p>}
        {veriYukleniyor && <p className="text-sm text-slate-500">Yükleniyor...</p>}

        {!veriYukleniyor && !veriHatasi && veri.length === 0 && (
          <p className="text-sm text-slate-500">Geçmiş fiyat verisi bulunamadı.</p>
        )}

        {!veriYukleniyor && veri.length > 0 && (
          <HisseGrafik veri={veri} hisseKodu={hisse.kod} anlikFiyat={anlikFiyat} gunlukDegisim={degisim} />
        )}
      </div>
    </div>
  );
}

export default HisseDetay
